import React, { createContext, useContext, ReactNode } from 'react';
import { createGlobalStyle } from 'styled-components';

type ThemeContextType = {
  colors: {
    primary: string;
    secondary: string;
    secondaryDark: string;
    accent: string;
    dark: string;
    light: string;
    neutral: string;
    backgroundColor: string;
  };
  fonts: {
    serif: string;
    sans: string;
  };
};

/**
 * Each colour has one job. Before, --primary was both the footer ground and the
 * button fill, and --dark was the same grey as --primary, so nothing could sit
 * above anything else.
 *
 *   primary    structure — the footer ground, rules, the device frames
 *   secondary  action    — buttons, links, the underline under a nav item
 *   accent     highlight — used sparingly, and never for something clickable
 *   dark       ink       — every heading and any body copy that isn't muted
 *   neutral    muted ink — supporting copy
 *   light      a warm white for insets; the old #f7f8fd was tinted blue
 */
/* Every colour here sits in the same warm band, hue 16-39. The greys used to
   sit at hue 240-273 - the opposite side of the wheel - which is why the footer
   read as violet next to the cream logo. They are now hue ~23 at the same
   lightness as before, so contrast is unchanged and only the temperature moves. */
const themeConfig: ThemeContextType = {
  colors: {
    primary: '#4a3f38',
    secondary: '#935f4c',
    secondaryDark: '#7d5040',
    accent: '#e6b794',
    dark: '#362f2b',
    light: '#faf6ef',
    neutral: '#77685f',
    backgroundColor: '#fdf4e3'
  },
  fonts: {
    serif: "'Cormorant Garamond', Georgia, serif",
    sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  }
};

const ThemeContext = createContext<ThemeContextType>(themeConfig);

export const useTheme = () => useContext(ThemeContext);

const GlobalStyle = createGlobalStyle`
  :root {
    --primary: ${themeConfig.colors.primary};
    --secondary: ${themeConfig.colors.secondary};
    --secondary-dark: ${themeConfig.colors.secondaryDark};
    --accent: ${themeConfig.colors.accent};
    --dark: ${themeConfig.colors.dark};
    --light: ${themeConfig.colors.light};
    --neutral: ${themeConfig.colors.neutral};
    --backgroundColor: ${themeConfig.colors.backgroundColor};
    --font-serif: ${themeConfig.fonts.serif};
    --font-sans: ${themeConfig.fonts.sans};
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: var(--font-sans);
    color: var(--dark);
    background-color: #ffffff;
    line-height: 1.5;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-serif);
    font-weight: 700;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  img {
    max-width: 100%;
  }

  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  @media (min-width: 768px) {
    .container {
      padding: 0 2rem;
    }
  }
`;

type ThemeProviderProps = {
  children: ReactNode;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <ThemeContext.Provider value={themeConfig}>
      <GlobalStyle />
      {children}
    </ThemeContext.Provider>
  );
};
